import type {
  AuthoredCard,
  CardColor,
  TypedValidatorResult,
  ValidatorIssue,
} from "../../../schemas"
import { SKILL_MAP } from "../content/skills"
import { computeSkillCostBreakdown, computeStatCost } from "../costs"

export const MAX_DERIVED_COST = 9
const HERO_MAX_DERIVED_COST: Record<1 | 2 | 3, number> = { 1: 3, 2: 5, 3: 10 }

function computeDerivedCost(card: AuthoredCard): number {
  const statCost = computeStatCost(card.stats.attack, card.stats.health)
  const skillCosts = computeSkillCostBreakdown(
    card.selectedSkills,
    SKILL_MAP,
    statCost + card.authoredCostInputs.secondaryColorSurcharge,
  )
  const raw =
    statCost +
    skillCosts.skillCostDelta +
    skillCosts.nonExhaustSurcharge +
    skillCosts.selfDestructSurcharge +
    card.authoredCostInputs.secondaryColorSurcharge
  return Math.ceil(raw)
}

function isAffinityAllowed(
  skillAffinity: string,
  primary: CardColor,
  secondary?: CardColor,
): boolean {
  if (skillAffinity === "colorless") return true
  if (skillAffinity === primary) return true
  if (secondary && skillAffinity === secondary) return true
  return false
}

export function validateCard(
  card: AuthoredCard,
): TypedValidatorResult<AuthoredCard> {
  const issues: ValidatorIssue[] = []

  // Skill affinity and existence
  const seenSkillIds = new Set<string>()
  for (const sel of card.selectedSkills) {
    if (seenSkillIds.has(sel.skillId)) {
      issues.push({
        code: "CARD_DUPLICATE_SKILL",
        message: `Skill "${sel.skillId}" selected more than once.`,
        path: `selectedSkills[${sel.skillId}]`,
        severity: "error",
      })
    }
    seenSkillIds.add(sel.skillId)

    const def = SKILL_MAP.get(sel.skillId)
    if (!def) {
      issues.push({
        code: "CARD_UNKNOWN_SKILL",
        message: `Unknown skill "${sel.skillId}".`,
        path: `selectedSkills[${sel.skillId}]`,
        severity: "error",
      })
      continue
    }

    if (
      !isAffinityAllowed(def.affinity, card.primaryColor, card.secondaryColor)
    ) {
      issues.push({
        code: "CARD_SKILL_AFFINITY_MISMATCH",
        message: `Skill "${sel.skillId}" (affinity: ${def.affinity}) is not available to a ${card.primaryColor}${card.secondaryColor ? `/${card.secondaryColor}` : ""} card.`,
        path: `selectedSkills[${sel.skillId}]`,
        severity: "error",
      })
    }

    // Validate modifier availability
    if (sel.mode === "non_exhausting" && def.nonExhaustSurcharge === 0) {
      issues.push({
        code: "CARD_MODIFIER_NOT_AVAILABLE",
        message: `Skill "${sel.skillId}" does not support the non_exhausting modifier.`,
        path: `selectedSkills[${sel.skillId}].mode`,
        severity: "error",
      })
    }
    if (sel.mode === "self_destruct" && def.selfDestructSurcharge === 0) {
      issues.push({
        code: "CARD_MODIFIER_NOT_AVAILABLE",
        message: `Skill "${sel.skillId}" does not support the self_destruct modifier.`,
        path: `selectedSkills[${sel.skillId}].mode`,
        severity: "error",
      })
    }
    if (sel.allyRequirement && !def.supportsAllyRequirement) {
      issues.push({
        code: "CARD_MODIFIER_NOT_AVAILABLE",
        message: `Skill "${sel.skillId}" does not support ally requirements.`,
        path: `selectedSkills[${sel.skillId}].allyRequirement`,
        severity: "error",
      })
    }
  }

  // Secondary color must differ from primary
  if (card.secondaryColor && card.secondaryColor === card.primaryColor) {
    issues.push({
      code: "CARD_DUPLICATE_COLOR",
      message: "Secondary color must differ from primary color.",
      path: "secondaryColor",
      severity: "error",
    })
  }

  // Secondary color surcharge consistency
  const expectsSurcharge =
    card.secondaryColor != null && card.primaryColor !== "colorless"
  const hasSurcharge = card.authoredCostInputs.secondaryColorSurcharge === 0.5
  if (expectsSurcharge && !hasSurcharge) {
    issues.push({
      code: "CARD_SURCHARGE_MISSING",
      message:
        "Cards with a secondary color must set secondaryColorSurcharge to 0.5.",
      path: "authoredCostInputs.secondaryColorSurcharge",
      severity: "error",
    })
  }
  if (!expectsSurcharge && hasSurcharge) {
    issues.push({
      code: "CARD_SURCHARGE_UNEXPECTED",
      message:
        "Cards without a paid secondary color must set secondaryColorSurcharge to 0.",
      path: "authoredCostInputs.secondaryColorSurcharge",
      severity: "error",
    })
  }

  // Derived cost cap
  const derivedCost = computeDerivedCost(card)
  const maxCost =
    card.kind === "hero"
      ? card.heroProgression
        ? HERO_MAX_DERIVED_COST[1]
        : HERO_MAX_DERIVED_COST[1]
      : MAX_DERIVED_COST
  if (derivedCost > maxCost) {
    issues.push({
      code: "CARD_COST_EXCEEDS_CAP",
      message: `Derived cost ${derivedCost} exceeds the maximum of ${maxCost}.`,
      severity: "error",
    })
  }

  // Warn if stored derivedCost is stale
  if (card.derivedCost != null && card.derivedCost !== derivedCost) {
    issues.push({
      code: "CARD_DERIVED_COST_STALE",
      message: `Stored derivedCost (${card.derivedCost}) does not match computed value (${derivedCost}). Resubmit to refresh.`,
      severity: "warning",
    })
  }

  const valid = issues.every((i) => i.severity !== "error")
  return { valid, value: valid ? card : undefined, issues }
}
